import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Shield, Swords, X } from "lucide-react";
import { useEffect } from "react";
import { MatchChatPanel } from "../../components/match/MatchChatPanel";
import { MatchCompletedView } from "../../components/match/MatchCompletedView";
import { MatchInProgressView } from "../../components/match/MatchInProgressView";
import type { MatchData } from "../../components/match/matchHelpers";
import { Button } from "../../components/ui";
import { getSocket } from "../../lib/socket";
import { trpc } from "../../lib/trpc";
import { MatchFlowView } from "../../match-flow/player/MatchFlowView";

export const Route = createFileRoute("/app/match/$shortId")({
  component: MatchPage,
  pendingComponent: () => (
    <div className="min-h-screen bg-surface-950 flex items-center justify-center">
      <Loader2 className="h-8 w-8 text-brand-500 animate-spin" />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-surface-950 flex flex-col items-center justify-center gap-4 text-white">
      <div className="h-16 w-16 rounded-full bg-surface-800 flex items-center justify-center">
        <Shield className="h-8 w-8 text-surface-500" />
      </div>
      <h2 className="text-xl font-display font-bold">Error Loading Match</h2>
      <p className="text-surface-400 text-center max-w-md">
        {error instanceof Error ? error.message : "Something went wrong."}
      </p>
      <Link to="/app">
        <Button variant="ghost">Go Home</Button>
      </Link>
    </div>
  ),
});

export function MatchPage() {
  const { shortId } = Route.useParams();
  const { data: match, isLoading, error } = trpc.match.get.useQuery({ shortId });

  // Fetch club for back-link
  const { data: club } = trpc.club.get.useQuery(
    { clubId: match?.clubId ?? "" },
    { enabled: !!match?.clubId },
  );

  return (
    <div className="min-h-screen bg-surface-950 text-white">
      {/* Subtle background texture */}
      <div className="fixed inset-0 noise-overlay pointer-events-none" />
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(38,217,181,0.04)_0%,_transparent_50%)] pointer-events-none" />

      {/* Minimal header */}
      <div className="relative z-10 flex items-center justify-between px-6 py-4">
        <Link
          to={club?.slug ? "/app/clubs/$slug" : "/app"}
          params={club?.slug ? { slug: club.slug } : undefined}
          className="flex items-center gap-2 text-sm text-surface-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {club?.name ? `Back to ${club.name}` : "Back"}
        </Link>
        {match && (
          <div className="flex items-center gap-2 text-sm text-surface-500">
            <Swords className="h-4 w-4" />
            <span className="font-mono">{shortId}</span>
          </div>
        )}
      </div>

      {/* Main content */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="relative z-10 max-w-6xl mx-auto px-6 pb-12"
      >
        <MatchPageContent
          matchId={match?.id ?? shortId}
          match={match}
          isLoading={isLoading}
          error={error}
          disputeWindowHours={club?.disputeWindowHours}
          clubSlug={club?.slug}
        />
      </motion.div>

      {match && <MatchChatPanel matchId={match.id} matchStatus={match.status} />}
    </div>
  );
}

type MatchPageContentProps = {
  matchId: string;
  match: MatchData | null | undefined;
  isLoading: boolean;
  error: unknown;
  disputeWindowHours?: number | null;
  clubSlug?: string;
};

function MatchPageContent({
  matchId,
  match,
  isLoading,
  error,
  disputeWindowHours,
  clubSlug,
}: MatchPageContentProps) {
  const utils = trpc.useUtils();

  // Real-time updates: listen for match events and invalidate query
  useEffect(() => {
    const socket = getSocket();
    socket.emit("join:match", matchId);

    const invalidateMatch = () => {
      void utils.match.get.invalidate();
    };
    const handleDisputed = () => {
      void utils.match.get.invalidate();
      void utils.dispute.getByMatch.invalidate({ matchId });
    };

    socket.on("match:reported", invalidateMatch);
    socket.on("match:completed", invalidateMatch);
    socket.on("match:disputed", handleDisputed);
    socket.on("match:cancelled", invalidateMatch);

    return () => {
      socket.off("match:reported", invalidateMatch);
      socket.off("match:completed", invalidateMatch);
      socket.off("match:disputed", handleDisputed);
      socket.off("match:cancelled", invalidateMatch);
      socket.emit("leave:match", matchId);
    };
  }, [matchId, utils]);

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 animate-pulse">
        <div className="h-16 w-16 rounded-full bg-surface-800" />
        <div className="h-6 w-48 rounded bg-surface-800" />
        <div className="h-4 w-64 rounded bg-surface-800" />
      </div>
    );
  }

  // Error
  if (error || !match) {
    const is403 =
      error &&
      typeof error === "object" &&
      "data" in error &&
      (error as { data?: { code?: string } }).data?.code === "FORBIDDEN";
    const is404 =
      error &&
      typeof error === "object" &&
      "data" in error &&
      (error as { data?: { code?: string } }).data?.code === "NOT_FOUND";

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="h-16 w-16 rounded-full bg-surface-800 flex items-center justify-center">
          <Shield className="h-8 w-8 text-surface-500" />
        </div>
        <h2 className="text-xl font-display font-bold text-white">
          {is403 ? "Access Denied" : is404 ? "Match Not Found" : "Error Loading Match"}
        </h2>
        <p className="text-surface-400 text-center max-w-md">
          {is403
            ? "You don't have permission to view this match."
            : is404
              ? "This match may have been cancelled or does not exist."
              : "Something went wrong loading this match."}
        </p>
        <Link
          to={clubSlug ? "/app/clubs/$slug" : "/app"}
          params={clubSlug ? { slug: clubSlug } : undefined}
        >
          <Button variant="ghost">Go Back</Button>
        </Link>
      </div>
    );
  }

  // Pending accept — shouldn't normally land here, but handle gracefully
  if (match.status === "pending_accept") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="h-8 w-8 text-brand-500 animate-spin" />
        <h2 className="text-xl font-display font-bold text-white">Waiting for Players</h2>
        <p className="text-surface-400">Players are still accepting the match...</p>
      </div>
    );
  }

  // In flow — render the match flow view
  if (match.status === "accepted" || match.status === "in_flow") {
    return (
      <MatchFlowView
        matchId={matchId}
        onComplete={() => utils.match.get.invalidate()}
        onFailed={() => utils.match.get.invalidate()}
      />
    );
  }

  // In progress — match is being played
  if (match.status === "in_progress") {
    return <MatchInProgressView match={match} />;
  }

  // Completed
  if (match.status === "completed") {
    return (
      <MatchCompletedView
        match={match}
        clubSlug={clubSlug}
        disputeWindowHours={disputeWindowHours}
      />
    );
  }

  // Cancelled
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
      <div className="h-16 w-16 rounded-full bg-surface-800 flex items-center justify-center">
        <X className="h-8 w-8 text-surface-500" aria-label="Match cancelled" />
      </div>
      <h2 className="text-xl font-display font-bold text-white">Match Cancelled</h2>
      <p className="text-surface-400">This match was cancelled.</p>
      <Link
        to={clubSlug ? "/app/clubs/$slug" : "/app"}
        params={clubSlug ? { slug: clubSlug } : undefined}
      >
        <Button variant="ghost">Back to Club</Button>
      </Link>
    </div>
  );
}
